import { AddButton, Button, CloseButton, IconButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { IAthlete, IGroup, ITeam } from '../../../../data/types';
import {
  archiveGroup,
  createGroupWithAthletes,
  updateGroupWithAthletes,
} from '../../../../data/writers/groups';
import { useEntities } from '../../../../hooks/useEntities';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { Icon } from '../../../shared/Icon';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { GroupForm } from './GroupForm';

interface IPopupProps {
  onClose: () => void;
  group?: IGroup;
}

const Popup = (props: IPopupProps): React.ReactElement => {
  const [group, setGroup] = React.useState<IGroup>(props.group);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [selectedAthletes, setSelectedAthletes] = React.useState<IAthlete[]>(
    []
  );
  const [selectedTeams, setSelectedTeams] = React.useState<ITeam[]>([]);

  const [aggregateOnCreate, setAggregateOnCreate] =
    React.useState<boolean>(false);

  const isEdit = Boolean(props.group);

  /// subscribe to entities
  const { athletes, teams, loading } = useEntities();

  /**
   * @description set the selected athletes and teams based on the supplied group (if this is an edit)
   */
  React.useEffect((): void => {
    if (props.group && !loading) {
      const mSelectedAthletes = athletes.filter((a: IAthlete): boolean =>
        a.groups?.includes(props.group.id)
      );
      const mSelectedTeams = teams.filter((t: IGroup): boolean =>
        group?.teams?.includes(t.id)
      );
      setSelectedAthletes(mSelectedAthletes);
      setSelectedTeams(mSelectedTeams);
    }
  }, [loading]);

  /**
   * @description handles updating the group to the selected teams, and updates athlete membership
   */
  const update = async (): Promise<void> => {
    setSaving(true);
    const groupData = {
      ...group,
      teams: selectedTeams.map((t: ITeam): string => t.id),
    };
    await updateGroupWithAthletes(
      groupData,
      selectedAthletes.map((a: IAthlete): string => a.id),
      athletes
    );
    props.onClose();
  };

  /**
   * @description handles creating the group to the selected teams, and any selected athletes to the group
   */
  const create = async (): Promise<void> => {
    setSaving(true);
    const groupData = {
      ...group,
      teams: selectedTeams.map((t: ITeam): string => t.id),
    };
    await createGroupWithAthletes(
      groupData,
      aggregateOnCreate,
      selectedAthletes.map((a: IAthlete): string => a.id)
    );
    props.onClose();
  };

  /**
   * @description when the selected teams change, remove any selected athletes that are not members
   *	of the currently selected teams
   */
  React.useEffect((): void => {
    const teamIds = selectedTeams.map((t: ITeam): string => t.id);
    const filteredAthletes = selectedAthletes.filter(
      (athlete: IAthlete): boolean =>
        athlete.teams.some((teamId: string): boolean =>
          teamIds.includes(teamId)
        )
    );
    if (filteredAthletes.length !== selectedAthletes.length) {
      setSelectedAthletes(filteredAthletes);
    }
  }, [selectedTeams.length]);

  /**
   * @description handles changes to the group
   * @param name
   * @param value
   */
  const onNameChange = (name: string, value: string): void => {
    setGroup({
      ...group,
      [name]: value,
    });
  };

  /**
   * @description handles archiving the group
   * @param name
   * @param value
   */
  const handleArchiveGroup = (): void => {
    archiveGroup({
      ...group,
      active: false,
    }).then((): void => props.onClose());
  };

  /**
   * @description selects all the available athletes
   */
  const selectAllAthletes = (): void =>
    setSelectedAthletes(athletesAvailableToSelect);
  /**
   * @description clears the currently selected athletes
   */
  const clearSelectedAthletes = (): void => setSelectedAthletes([]);

  /**
   * @description selects all the available teams
   */
  const selectAllTeams = (): void => setSelectedTeams(teams);

  /**
   * @description clears the currently selected teams
   */
  const clearSelectedTeams = (): void => setSelectedTeams([]);

  /**
   * @description handles the selection/de-selection of an athlete
   * @param id
   * @param checked
   */
  const athleteSelectionHandler = (
    athlete: IAthlete,
    checked: boolean
  ): void => {
    let mSelectedAthletes = [...selectedAthletes];
    if (checked) {
      mSelectedAthletes.push(athlete);
    } else {
      mSelectedAthletes = mSelectedAthletes.filter(
        (a: IAthlete): boolean => a.id !== athlete.id
      );
    }
    setSelectedAthletes(mSelectedAthletes);
  };

  /**
   * @description handles the selection/de-selection of a team
   * @param id
   * @param checked
   */
  const teamSelectionHandler = (team: ITeam, checked: boolean): void => {
    let mSelectedTeams = [...selectedTeams];
    if (checked) {
      mSelectedTeams.push(team);
    } else {
      mSelectedTeams = mSelectedTeams.filter(
        (t: ITeam): boolean => t.id !== team.id
      );
    }
    setSelectedTeams(mSelectedTeams);
  };

  /**
   * @description the list of athletes that are available to select because
   *	they are members of at least one selected team
   */
  const athletesAvailableToSelect: IAthlete[] =
    React.useMemo((): IAthlete[] => {
      return athletes.filter((a: IAthlete): boolean =>
        a.teams.some((id: string): boolean =>
          selectedTeams.map((t: ITeam): string => t.id).includes(id)
        )
      );
    }, [athletes?.length, selectedTeams]);

  return (
    <PopupCard onClose={props.onClose} noClickOutside size='large'>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>{isEdit ? 'Edit' : 'Add'} Group</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {(loading || saving) && <LinearLoading />}
          {!loading && (
            <GroupForm
              athletes={athletesAvailableToSelect}
              clearAllAthletes={clearSelectedAthletes}
              selectAllAthletes={selectAllAthletes}
              selectedAthletes={selectedAthletes}
              athleteSelectionHandler={athleteSelectionHandler}
              teams={teams}
              selectedTeams={selectedTeams}
              clearAllTeams={clearSelectedTeams}
              selectAllTeams={selectAllTeams}
              teamSelectionHandler={teamSelectionHandler}
              group={group}
              onNameChange={onNameChange}
              aggregateOnCreate={aggregateOnCreate}
              setAggregateOnCreate={setAggregateOnCreate}
              isEdit={isEdit}
            />
          )}
        </CardBody>
        <CardFooter
          surface={8}
          style={{
            paddingTop: '10px',
            paddingBottom: '10px',
          }}
        >
          <CardFooterLeft>
            {isEdit ? (
              <Button
                border={'none'}
                color={'danger'}
                flavor={'none'}
                title='Archive Group'
                onClick={handleArchiveGroup}
               />
            ) : null}
          </CardFooterLeft>
          <CardFooterRight>
            <Button
              border={'none'}
              color={'light'}
              flavor={'primary'}
              type='submit'
              onClick={isEdit ? update : create}
              title={isEdit ? 'Update Group' : 'Create Group'}
              disabled={
                saving ||
                !group ||
                group?.name?.length < 1 ||
                selectedTeams?.length < 1
              }
              icon='check'
              style={{ whiteSpace: 'nowrap' }}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  group?: IGroup;
}

const ManageGroupPopup = withRouter((props: IProps): React.ReactElement => {
  const isEdit = Boolean(props.group);
  const openDialog = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    openPopup(
      <Popup group={props.group} onClose={closePopup(props.history)} />,
      props.history,
      isEdit ? 'group-edit' : 'group-add'
    );
  };

  return isEdit ? (
    <IconButton icon='edit' onClick={openDialog} title='Manage Group' />
  ) : (
    <AddButton onClick={openDialog} />
  );
});

export { ManageGroupPopup };
